<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, shallowRef, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import {
  i18nInjectionKey,
  provideRichTextExtensions,
  provideRichTextVariables,
  useNxToasts,
} from "@blpde/nx-ui";
import {
  NxCommandPalette,
  NxCommandRegistryKey,
  NxSidebarEmbeddedKey,
  createNxCommandRegistry,
} from "@blpde/nx-ui";
import {
  ConnectPermissionsKey,
  DriveServiceKey,
  loadDriveFormats,
  setDriveFormatApps,
  ErpRecordPickerKey,
  NxSocketClient,
  NxSocketKey,
  attachSocketAuthentication,
  assistantChannelKey,
  connectTileId,
  connectNotificationsKey,
  createAssistantChannel,
  createHttpSearchDictionaryClient,
  createMasterSearchDictionaryApi,
  createMasterDictionaryActions,
  provideSearchDictionaryClient,
  provideSearchDictionaryActions,
  useConnectSettingsCatalog,
  useSearchDictionaryFilters,
} from "@blpde/connect";
import {
  ensureValidStoredSession,
  handleAuthFailure,
  isAuthenticated,
  useShellUser,
} from "@/auth";
import { holdAccountApplications } from "@/applications";
import { listenForScopeSwitches } from "@/scope";
import { originKey, reportWriteFailure, resolveWriteFailure } from "@blpde/connect/http";
import "@blpde/nx-ui/style.css";
import "@blpde/nx-ui/theme/color/color-dark.css";
import "@/styles/tokens.css";
import "@/styles/base.css";
import "@/styles/shell.css";
// Side-effect import: applies the persisted theme (data-theme on <html>) at
// startup so a saved dark choice survives reloads without a flash.
import "@/settings";

import ShellLayout from "@/layouts/ShellLayout.vue";
import { useTilesEnabled } from "@/tiles/tileNav";
import { DriveFilePicker, createDriveFilePicker, fetchDriveFormats } from "@drive/connect/shell";
import { installDriveFormicaBridge } from "@/services/driveBridge";
import {
  buildGlobalCommands,
  buildModuleCommands,
} from "@/integration/commands";
import {
  buildNavCommands,
  buildSettingsCommands,
  buildSettingsListCommands,
  buildShellCommands,
  buildShellSettingsCommands,
  type ShellCommandContext,
} from "@/integration/shellCommands";
import { apps, appAllowed, appForPath } from "@/integration";
import { installConnectApp } from "@/integration/loader";
import { loadAppSettingsModules, type SettingsHubModule } from "@/settingsHub/hub";
import { useAccountApplications } from "@/applications";
import { useModuleAvailability } from "@/modules";
import { useEditorExtensions } from "@/editorExtensions";
import { companyLabel, tenantLabel, useSessionScope } from "@/scope";
import { setShellLocale, setShellTheme, useShellTheme } from "@/settings";
import { signOut } from "@/auth";
import { bindSocketLocale } from "@/socketLocale";
import { muxUrl } from "@/muxUrl";
import { createNotificationChannel } from "@/notificationChannel";
import {
  buildUniversalSources,
  buildUniversalSuggester,
  MASTER_MODULE,
  type SearchModule,
} from "@/search/universal";
import { hydrateSearchSettings, useSearchSettings } from "@/search/settings";
import { createPaletteMemoryStore } from "@/search/memory";
import {
  ErpRecordPickerDialog,
  createErpRecordPicker,
  erpDefinitionsLoaded,
  usePermissionStore,
} from "@erp/connect/shell";
import { ConnectAppI18nScope } from "@/i18n/appI18n";
import { watchErpDefinitionsLoading } from "@/integration/erpDefinitions";

const { t, d, locale } = useI18n({ inheritLocale: true, useScope: "global" });
provide(i18nInjectionKey, { t, d, locale });

// Sub-app sidebars detect this and hide their footer (account/logout):
// the shell already renders a global account menu in the left rail.
provide(NxSidebarEmbeddedKey, true);
provide(assistantChannelKey, createAssistantChannel());

const router = useRouter();
const commands = createNxCommandRegistry();
provide(NxCommandRegistryKey, commands);
const erpPermissions = usePermissionStore();
// The ERP's screen definitions load as soon as the permission snapshot is in (the
// command palette reads them before the ERP is opened), and again after a re-login.
// With tabs and tiles the page and every tile are a Connect of their own: what only
// the palette needs waits until a palette opens, and an ERP tile loads its own (I-3641).
const tilesEnabled = useTilesEnabled();
const inTile = connectTileId() !== null;
const paletteNeedsIt = () =>
  !(inTile || tilesEnabled.value) || commands.openCount.value > 0;
const stopErpDefinitionsWatch = watchErpDefinitionsLoading(
  () => erpPermissions.loaded && paletteNeedsIt(),
);
// Hosted apps check permissions through this instead of importing ERP's store
// across bundles (I-1786).
provide(ConnectPermissionsKey, {
  can: (requirement, mode) => erpPermissions.can(requirement, mode),
});

// Shell globals + every hosted module's own commands. Each module contributes
// via its AppEntry.commands factory, so wiring a new module's palette entries
// means editing that module's integration file — never this one.
// The shell's tile commands come and go with `shell.tiles.enabled`.
const commandCtx = {
  router,
  t,
  can: (requirement: string | string[], mode?: "all" | "any") =>
    erpPermissions.can(requirement, mode),
};
const { theme } = useShellTheme();
const scope = useSessionScope();
const shellCtx: ShellCommandContext = {
  ...commandCtx,
  openPalette: (query) => commands.open(query),
  companies: async () => {
    await scope.load();
    const data = scope.scopes.value;
    return scope.tenants.value.flatMap((tenant) =>
      tenant.companies.map((company) => ({
        tenantID: tenant.id,
        companyID: company.id,
        label: companyLabel(company),
        hint: scope.tenants.value.length > 1 ? tenantLabel(tenant) : undefined,
        active: data?.tenantID === tenant.id && data?.companyID === company.id,
      })),
    );
  },
  switchCompany: (tenantID, companyID) => scope.switchTo(tenantID, companyID),
  setLocale: (next) => setShellLocale(next),
  toggleTheme: () => setShellTheme(theme.value === "dark" ? "light" : "dark"),
  signOut: () => signOut(),
  copy: (text) => void navigator.clipboard?.writeText(text).catch(() => {}),
  reload: () => window.location.reload(),
  currentUrl: () => window.location.href,
};

// App settings topics live in the apps' lazily loaded modules: they are
// loaded once the palette is first opened, not at startup — and with them the
// apps' sections of the one settings list (I-2510; Connect's own are there
// from the start).
const appSettingsModules = shallowRef<SettingsHubModule[]>([]);
const settingsCatalog = useConnectSettingsCatalog();
let appSettingsRequested = false;
function loadAppSettingsCommands() {
  if (appSettingsRequested || !isAuthenticated()) return;
  appSettingsRequested = true;
  void settingsCatalog.load();
  void loadAppSettingsModules(
    apps.filter((app) => isEntitled(app.id) && appAllowed(app, commandCtx.can)),
    (id) => installConnectApp(router, id),
  ).then((modules) => {
    appSettingsModules.value = modules;
  });
}

let unregisterCommands: () => void = () => {};
const stopCommandWatch = watch(
  [
    () => erpPermissions.permissions.join("\n"),
    // The Stammdaten jumps are cockpit tiles of the ERP's served definitions.
    () => erpDefinitionsLoaded.value,
    () => locale.value,
    () => router.currentRoute.value.path,
    appSettingsModules,
    settingsCatalog.sections,
    tilesEnabled,
  ],
  () => {
    unregisterCommands();
    const base = [
      ...buildGlobalCommands(commandCtx),
      ...buildModuleCommands(commandCtx),
      ...buildShellCommands(shellCtx),
      ...buildShellSettingsCommands(commandCtx),
      ...buildSettingsCommands(appSettingsModules.value, commandCtx),
      ...buildSettingsListCommands(settingsCatalog.sections.value, commandCtx),
    ];
    const labels = new Set(base.map((c) => c.label.toLowerCase()));
    unregisterCommands = commands.register([
      ...base,
      ...buildNavCommands(commandCtx, labels),
    ]);
  },
  { immediate: true },
);

// Universal search (I-2348): one source per module that answers
// /v1/search/kinds, in the account's order, and one suggester for the
// modules that offer spelling suggestions (I-3024). Rebuilt when the modules,
// the settings or the language change, and on every open (the catalogs are
// cached, so this is cheap; it also picks up a login after the shell booted).
// One dictionary for the shell and every app. The session instance remembers
// a word until the personal dictionary in master exists; it is not a setting.
const { settings: searchSettings } = useSearchSettings();
const { isEntitled, authoritative: entitlementsKnown } = useAccountApplications();
const { isAvailable, loaded: modulesKnown } = useModuleAvailability();
const searchModules = computed<SearchModule[]>(() => [
  ...apps
    .filter(
      (app) =>
        app.id === app.moduleKey &&
        isEntitled(app.id) &&
        isAvailable(app.moduleKey) &&
        appAllowed(app, commandCtx.can),
    )
    .map((app) => ({ key: app.moduleKey, label: t(app.labelKey) })),
  { key: MASTER_MODULE, label: t("connect.search.people") },
]);
// Editor extensions of the apps (I-2799) — the ERP's text blocks — for every
// editor of every app, while the app is usable. Unlike the app tiles they do
// not fail open: an account whose grants or modules are not known yet gets no
// button that would call a module it may not use.
provideRichTextExtensions(
  useEditorExtensions({
    usable: (app) =>
      entitlementsKnown.value &&
      modulesKnown.value &&
      isEntitled(app.id) &&
      isAvailable(app.moduleKey) &&
      appAllowed(app, commandCtx.can),
    locale: () => locale.value,
  }),
);
// Who writes, for every editor: the comment's `{{user.name}}` (I-2799). The
// `user.` values belong to every place, so they never make a place one that
// refuses a text block it cannot fill.
const { user: shellUser } = useShellUser();
provideRichTextVariables(() => {
  const user = shellUser.value;
  if (!user?.email) return {};
  return { "user.name": user.name || user.email, "user.email": user.email };
});
let unregisterSources: (() => void)[] = [];
let sourcesSeq = 0;
// The search's dictionary is a setting like any other (I-3024): words, hidden
// words and learned corrections are merge patches of `spell_check`, and the
// filters read the resolved document — no dictionary client, no request of
// its own.
const dictionaryActions = createMasterDictionaryActions();
provideSearchDictionaryActions(dictionaryActions);
// The read routes of the dictionary tabs (sources, licences, explain, thesaurus):
// one client for the shell and the admin app it hosts, so the tabs share it
// instead of each creating its own.
provideSearchDictionaryClient(createHttpSearchDictionaryClient(createMasterSearchDictionaryApi()));
const dictionaryFilters = useSearchDictionaryFilters();
// A refused „nicht mehr vorschlagen“ of the palette shows as the standard
// write-failure toast; a later try that works takes it away again.
const { showToast, dismiss } = useNxToasts();
const searchNeverSuggestOrigin = originKey(useId(), "action", "never-suggest");
const searchWriteReporter = {
  failed: (cause: unknown) =>
    reportWriteFailure(cause, {
      origin: searchNeverSuggestOrigin,
      t,
      toast: { showToast, dismiss },
      kind: "update",
    }),
  resolved: () => resolveWriteFailure({ origin: searchNeverSuggestOrigin }),
};
async function rebuildSearchSources() {
  const seq = ++sourcesSeq;
  if (!isAuthenticated() || !paletteNeedsIt()) return;
  const searchContext = {
    modules: searchModules.value,
    settings: searchSettings.value,
    locale: locale.value,
    t,
    open: (target: string) => void router.push(target),
    reopen: (query: string) => commands.open(query),
    formatDate: (iso: string) => d(new Date(iso), "short"),
    dictionaryFilters: () => ({
      hidden: dictionaryFilters.hidden.value,
      personal: dictionaryFilters.personal.value,
    }),
    writeReporter: searchWriteReporter,
  };
  const sources = await buildUniversalSources(searchContext);
  if (seq !== sourcesSeq) return;
  const suggester = buildUniversalSuggester(searchContext, dictionaryActions);
  unregisterSources.forEach((off) => off());
  unregisterSources = [
    ...sources.map((source) => commands.registerSource(source)),
    commands.registerSuggester(suggester),
  ];
}
const stopSourceWatch = watch(
  [searchModules, searchSettings, () => locale.value, commands.openCount],
  () => void rebuildSearchSources(),
  { deep: true, immediate: true },
);
// A login (or token change) makes the catalogs reachable: build the sources
// right away, so the first ⌘K after signing in already knows the keywords.
const onAuthChanged = () => void rebuildSearchSources();
window.addEventListener("formica:auth-changed", onAuthChanged);

// The open app ranks first; remembered records reopen through the router.
const stopContextWatch = watch(
  () => router.currentRoute.value.path,
  (path) => {
    commands.context.value = { app: appForPath(path)?.id };
  },
  { immediate: true },
);
commands.navigate.value = (href) => void router.push(href);
commands.resolveUrl.value = (href) => router.resolve(href).href;

let memoryLoaded = false;
const stopOpenWatch = watch(commands.openCount, () => {
  loadAppSettingsCommands();
  if (!memoryLoaded && isAuthenticated()) {
    memoryLoaded = true;
    void commands.useMemoryStore(createPaletteMemoryStore());
    void hydrateSearchSettings();
  }
});

/**
 * Shared multiplexed WebSocket — ported 1:1 from the legacy connect/ project.
 * All sub-apps tunnel through this single WebSocket connection (PubSub channels +
 * RPC paths). Sub-apps must NOT open their own WebSockets when running in
 * the Connect shell.
 *
 * URL resolution: the same API base the HTTP client uses (`getApiOrigin`:
 * `?url=<base64>` → `formica.api.baseUrl` → `window.__API_URL__` →
 * `VITE_API_URL` → page host on :7680), read on every connect — so the socket
 * follows the server the user chose on the login page. No socket opens
 * without a session; the bearer is owned by Connect and only ever sent to the
 * origin that issued it. Per-app legacy tokens are only mirrors for hosted
 * modules.
 */
const socket = new NxSocketClient(muxUrl());
// The mux URL carries the UI language (?lang=); a language switch reconnects it once.
const detachSocketLocale = bindSocketLocale(socket, muxUrl);
socket.appKey = "formica-connect";
socket.appVersion = __APP_VERSION__;
const detachSocketAuthentication = attachSocketAuthentication(socket, handleAuthFailure);
NxSocketClient.setInstance(socket);
provide(NxSocketKey, socket);
// Master's notifications for the hosted apps (I-2915): the calendar's
// reminder dialog reads and acts on them through the shell's store.
provide(connectNotificationsKey, createNotificationChannel(socket));

const drivePicker = createDriveFilePicker();
provide(DriveServiceKey, drivePicker.service);
installDriveFormicaBridge(drivePicker.service);

// Which app opens which file: the catalog needs a session, and a login does not
// always reload the page, so every navigation may be the one that can fetch it.
setDriveFormatApps(apps);
function loadFormatCatalog() {
  if (isAuthenticated()) void loadDriveFormats(fetchDriveFormats);
}
const stopFormatCatalogLoad = router.afterEach(loadFormatCatalog);

// Available only while the ERP module has a connected client.
const erpRecordPicker = createErpRecordPicker({ available: () => isAvailable("erp") });
provide(ErpRecordPickerKey, erpRecordPicker.service);

function onKey(e: KeyboardEvent) {
  // ⌘K / Ctrl-K — always toggles. The previous "/" shortcut was removed:
  // it fired while the user was typing inside fields without a focus check
  // and made plain "/" characters in mail subjects / message bodies open
  // the palette mid-sentence.
  if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
    e.preventDefault();
    commands.toggle();
  }
}

// A company switch in another tab of this session reloads this one too.
let stopScopeListener: () => void = () => {};

onMounted(() => {
  window.addEventListener("keydown", onKey);
  stopScopeListener = listenForScopeSwitches();
  socket.connect();
  // Shares the router guard's one-shot validation instead of pulling
  // /oauth/userinfo a second time on every page load; it hydrates the user pill
  // on the way through.
  void ensureValidStoredSession();
  loadFormatCatalog();
  // Keep app grants warm for the whole shell session so Home → Calc (etc.)
  // does not race a first-time entitlement fetch and bounce back to home.
  if (isAuthenticated()) {
    holdAccountApplications();
    void erpPermissions.ensureLoaded().catch(() => {});
  }
});
onBeforeUnmount(() => {
  stopFormatCatalogLoad();
  stopErpDefinitionsWatch();
  stopCommandWatch();
  stopSourceWatch();
  stopContextWatch();
  stopOpenWatch();
  window.removeEventListener("formica:auth-changed", onAuthChanged);
  unregisterCommands();
  unregisterSources.forEach((off) => off());
  window.removeEventListener("keydown", onKey);
  stopScopeListener();
  detachSocketAuthentication();
  detachSocketLocale();
  socket.stop();
});
</script>

<template>
  <ShellLayout>
    <RouterView />
  </ShellLayout>
  <NxCommandPalette />
  <!-- Drive's picker serves every app but speaks Drive's own texts (I-2284). -->
  <ConnectAppI18nScope app="drive">
    <DriveFilePicker :handle="drivePicker" />
  </ConnectAppI18nScope>
  <!-- The ERP's record picker serves every app (Calc's quote calculation, I-3178).
       Only once it is asked for: loading it loads the ERP's whole definitions. -->
  <ConnectAppI18nScope v-if="erpRecordPicker.state.request" app="erp">
    <ErpRecordPickerDialog :handle="erpRecordPicker" />
  </ConnectAppI18nScope>
</template>
